import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TextAbbreviationDocument, TextAbbreviationDocumentsArray, TextAbbreviationJSON } from 'models';
import { selectAbbreviationsSortedData } from '../store/selectors';

function getTermText(term: TextAbbreviationJSON['term']): string | undefined {
  if (typeof term === 'string') {
    return term;
  }
  if (term && typeof term === 'object' && Array.isArray(term.part)) {
    return term.part.map(p => (typeof p === 'string' ? p : p._.trim())).join('').trim();
  }

  return undefined;
}

export default function useFilteredAbbreviations(termToSearch: string | undefined): {
  abbreviationsList: TextAbbreviationDocumentsArray;
  caseCorrectedTerm: string;
} {
  const textAbbreviationDocuments: TextAbbreviationDocumentsArray = useSelector(selectAbbreviationsSortedData);

  return useMemo(() => {
    if (!termToSearch) {
      return { abbreviationsList: [], caseCorrectedTerm: '' };
    }

    const abbreviationsListFiltered = textAbbreviationDocuments
      .filter(
        ({ abbreviation }) => {
          const termText = getTermText(abbreviation?.term);

          return termText?.trim().toLowerCase() === termToSearch?.trim().toLowerCase();
        },
      );

    // Remove duplicates: same term + same definition → keep the one with the lowest Vault documentId
    const abbreviationsMap = new Map<string, TextAbbreviationDocument>();
    abbreviationsListFiltered.forEach((doc) => {
      const key = doc.abbreviation?.definition?.trim().toLowerCase();
      if (key === undefined) {
        return;
      }

      const existingDoc = abbreviationsMap.get(key);
      if (!existingDoc || doc.documentId < existingDoc.documentId) {
        abbreviationsMap.set(key, doc);
      }
    });

    const abbreviationsList = Array.from(abbreviationsMap.values());
    const [firstTextAbbreviationDocument] = abbreviationsList;
    // When first abbreviation is created user is not able to create variants of term anymore
    const caseCorrectedTerm = getTermText(firstTextAbbreviationDocument?.abbreviation?.term) ?? termToSearch;

    return {
      abbreviationsList,
      caseCorrectedTerm,
    };
  }, [termToSearch, textAbbreviationDocuments]);
}
